import React, { useEffect } from 'react';
import { Button, ProfessionalDateRange } from '../../../shared/components';
import { Search, Filter, Building2 } from 'lucide-react';

/**
 * Componente de filtros para movimientos de caja
 * Permite filtrar por rango de fechas y sede
 */
const CashMovementsFilters = ({
  filters = {},
  onFiltersChange,
  onClearFilters,
  onSearch,
  loading = false
}) => {
  // Obtener la fecha de hoy en formato YYYY-MM-DD (timezone local)
  const getTodayString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const day = today.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Inicializar filtros con fecha de hoy si no existen
  useEffect(() => {
    if (!filters.startDate && !filters.endDate) {
      const today = getTodayString();
      onFiltersChange({
        ...filters,
        startDate: today,
        endDate: today
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manejar cambio de rango de fechas
  const handleDateRangeChange = (startDate, endDate) => {
    onFiltersChange({
      ...filters,
      startDate: startDate || '',
      endDate: endDate || ''
    });
  };

  // Manejar cambio de sede
  const handleSedeChange = (e) => {
    onFiltersChange({
      ...filters,
      sede: e.target.value
    });
  };

  // Manejar aplicación de filtros
  const handleApplyFilters = () => {
    onSearch();
  };

  return (
    <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-200/30 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
            <Filter className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 tracking-tight">
              Filtros de Consulta
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Define el periodo y ubicación para consultar movimientos de caja
            </p>
          </div>
        </div>
        
        {/* Status indicator */}
        {(filters.startDate || filters.endDate || filters.sede) && (
          <div className="flex items-center space-x-2 text-sm">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
            <span className="text-blue-600 font-medium">Filtros activos</span>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Rango de Fechas */}
        <div className="xl:col-span-2">
          <ProfessionalDateRange
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateRangeChange}
            disabled={loading}
            label="Periodo de Consulta"
            className="w-full"
          />
        </div>

        {/* Sede */}
        <div className="flex flex-col h-full">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Building2 className="w-4 h-4 inline mr-1" />
            Sede
          </label>
          <div className="relative flex-1">
            <select
              id="sede"
              value={filters.sede || ''}
              onChange={handleSedeChange}
              className="w-full h-[72px] px-4 py-4 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white appearance-none pr-10 transition-all duration-200 hover:border-blue-400 hover:shadow-md"
              disabled={loading}
            >
              <option value="">Todas las sedes</option>
              <option value="Belgrano">🏢 Belgrano</option>
              <option value="Caballito">🏢 Caballito</option>
              <option value="Palermo">🏢 Palermo</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex flex-col h-full">
          <div className="block text-sm font-medium text-gray-700 mb-2 opacity-0">
            Acciones
          </div>
          <div className="flex flex-col space-y-3 flex-1 justify-start">
            <Button
              onClick={handleApplyFilters}
              disabled={loading}
              className="w-full h-[34px] bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium px-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-[1.02] text-sm flex items-center justify-center"
            >
              <Search className="w-4 h-4 mr-2" />
              {loading ? 'Buscando...' : 'Buscar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashMovementsFilters;